import { motion } from "framer-motion";
import {
  FiRefreshCw,
  FiCheckCircle,
  FiAlertCircle,
  FiClock,
} from "react-icons/fi";

const returnSteps = [
  {
    title: "Contact Us",
    description:
      "Reach out to our customer service team within 30 days of receiving your order.",
    icon: FiClock,
  },
  {
    title: "Get Authorization",
    description:
      "Receive return authorization and shipping instructions from our team.",
    icon: FiCheckCircle,
  },
  {
    title: "Ship Items Back",
    description:
      "Send the items back in their original packaging with all accessories.",
    icon: FiRefreshCw,
  },
  {
    title: "Receive Refund",
    description:
      "Once received, we'll process your refund within 5-7 business days.",
    icon: FiAlertCircle,
  },
];

export default function ReturnsExchanges() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-50 to-white pt-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-[#DB5461] mb-6">
            Returns & Exchanges
          </h1>
          <div className="w-24 h-1 bg-[#DB5461] mx-auto rounded-full mb-6" />
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            We want you to be completely satisfied with your purchase. Learn
            about our return and exchange process.
          </p>
        </motion.div>

        {/* Return Process Steps */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mb-16"
        >
          <h2 className="text-2xl font-semibold text-[#DB5461] mb-8 text-center">
            Return Process
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {returnSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 * index }}
                  className="bg-white p-6 rounded-xl shadow-sm border border-gray-100"
                >
                  <div className="flex items-center mb-4">
                    <div className="p-3 bg-[#DB5461]/10 rounded-full">
                      <Icon className="h-6 w-6 text-[#DB5461]" />
                    </div>
                    <h3 className="ml-4 text-xl font-semibold text-[#DB5461]">
                      {step.title}
                    </h3>
                  </div>
                  <p className="text-gray-600 ml-12">{step.description}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Return Conditions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-16"
        >
          <div className="px-6 py-4 bg-[#DB5461]/10">
            <h2 className="text-2xl font-semibold text-[#DB5461]">
              Return Conditions
            </h2>
          </div>
          <div className="p-6">
            <ul className="space-y-4">
              <li className="flex items-start">
                <FiCheckCircle className="h-6 w-6 text-[#DB5461] mt-0.5 mr-3 flex-shrink-0" />
                <p className="text-gray-600">
                  Items must be unused and in their original packaging
                </p>
              </li>
              <li className="flex items-start">
                <FiCheckCircle className="h-6 w-6 text-[#DB5461] mt-0.5 mr-3 flex-shrink-0" />
                <p className="text-gray-600">
                  Products must be sealed and in resalable condition
                </p>
              </li>
              <li className="flex items-start">
                <FiCheckCircle className="h-6 w-6 text-[#DB5461] mt-0.5 mr-3 flex-shrink-0" />
                <p className="text-gray-600">
                  Original receipt or proof of purchase is required
                </p>
              </li>
              <li className="flex items-start">
                <FiCheckCircle className="h-6 w-6 text-[#DB5461] mt-0.5 mr-3 flex-shrink-0" />
                <p className="text-gray-600">
                  Returns must include all accessories and free items (if any)
                </p>
              </li>
              <li className="flex items-start">
                <FiAlertCircle className="h-6 w-6 text-[#DB5461] mt-0.5 mr-3 flex-shrink-0" />
                <p className="text-gray-600">
                  Items marked as "Final Sale" cannot be returned
                </p>
              </li>
            </ul>
          </div>
        </motion.div>

        {/* Exchange Process */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <h2 className="text-2xl font-semibold text-[#DB5461] mb-6">
            Exchange Process
          </h2>
          <div className="space-y-6">
            <p className="text-gray-600">
              To exchange an item for a different product, follow our return
              process and place a new order for the desired item. Once we
              receive your return, we'll process your refund.
            </p>

            <div className="bg-[#DB5461]/10 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-[#DB5461] mb-2">
                Contact Information
              </h3>
              <div className="space-y-2 text-gray-600">
                <p>Email: returns@acquwaanaturals.com</p>
                <p>Phone: +233 XX XXX XXXX</p>
                <p>Hours: Monday - Friday, 9am - 5pm GMT</p>
              </div>
            </div>

            <div className="border-t border-gray-100 pt-6">
              <h3 className="text-lg font-semibold text-[#DB5461] mb-2">
                Need Help?
              </h3>
              <p className="text-gray-600">
                If you have any questions about returns or exchanges, please{" "}
                <a
                  href="/contact"
                  className="text-[#DB5461] hover:text-[#DB5461]/80"
                >
                  contact our support team
                </a>
                . We're here to help!
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
